import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { __ } from '@design-system/i18n';
import { useBreakpoint, useTheme } from '@design-system/theme';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { calculateSelectMenuPlacement, formatOptions, getOptionLabel, getOptionValue, getReactSelectComponent, getReactSelectCustomComponents, getReactSelectCustomStyles, getSelectedOptions, MenuPlacementOptions, } from './utils';
import { BaseInput } from '../BaseInput';
const noOptionsMessageFn = () => __('No options');
const InputComponent = (_a, ref) => {
    var { ariaDescribedBy, ariaLabel, components, defaultValue, error, icon, iconName, id, isAsync, isClearable, isCreatable, isDisabled, isLoading, isMulti, isSearchable, menuPlacement: menuPlacementInput = MenuPlacementOptions.Auto, menuPortalTarget = document.body, name, noOptionsMessage = noOptionsMessageFn, onChange, options = [], placeholder, styles, value, variant = 'normal' } = _a, props = __rest(_a, ["ariaDescribedBy", "ariaLabel", "components", "defaultValue", "error", "icon", "iconName", "id", "isAsync", "isClearable", "isCreatable", "isDisabled", "isLoading", "isMulti", "isSearchable", "menuPlacement", "menuPortalTarget", "name", "noOptionsMessage", "onChange", "options", "placeholder", "styles", "value", "variant"]);
    const errorMessage = typeof error === 'string' ? error : undefined;
    const inputId = id ? `${id}-input` : undefined;
    const theme = useTheme();
    const themeInput = theme.input[variant];
    const breakpoint = useBreakpoint();
    const formattedOptions = formatOptions({ options });
    const isControlled = value !== undefined;
    const selectedOptions = isAsync
        ? value
        : getSelectedOptions({
            isControlled,
            isCreatable,
            isMulti,
            options: formattedOptions,
            selectedValue: value,
        });
    const selectedDefaultOptions = isAsync
        ? defaultValue
        : getSelectedOptions({
            isControlled,
            isCreatable,
            isMulti,
            options: formattedOptions,
            selectedValue: defaultValue,
        });
    const customStyles = useMemo(() => getReactSelectCustomStyles({
        breakpoint,
        styles,
        theme,
        themeInput,
    }), [breakpoint, styles, theme, themeInput]);
    const customComponents = useMemo(() => getReactSelectCustomComponents({
        components,
        icon,
        iconName,
        theme,
        themeInput,
    }), [components, icon, iconName, theme, themeInput]);
    const handleNoOptionsMessage = useCallback(({ inputValue }) => (_jsx(NoOptionsMessageStyled, { children: noOptionsMessage === null || noOptionsMessage === void 0 ? void 0 : noOptionsMessage({ inputValue }) })), [noOptionsMessage]);
    const containerRef = useRef(null);
    const [menuPlacement, setMenuPlacement] = useState(menuPlacementInput);
    const SelectComponent = getReactSelectComponent({
        async: isAsync,
        creatable: isCreatable,
    });
    const calculateMenuPlacement = useCallback(() => {
        if (menuPlacementInput === MenuPlacementOptions.Auto) {
            const recalculatedMenuPlacement = calculateSelectMenuPlacement({
                menuSize: theme.dropdown.maxHeight,
                options,
                ref: containerRef,
            });
            if (recalculatedMenuPlacement !== menuPlacement) {
                setMenuPlacement(recalculatedMenuPlacement);
            }
        }
    }, [menuPlacement, menuPlacementInput, theme.dropdown.maxHeight, options]);
    return (_jsx("div", { ref: containerRef, children: _jsx(SelectComponent, Object.assign({ "aria-describedby": ariaDescribedBy, "aria-errormessage": errorMessage, "aria-label": ariaLabel, components: customComponents, defaultValue: selectedDefaultOptions, getOptionLabel: getOptionLabel, getOptionValue: getOptionValue, id: id, inputId: inputId, isClearable: isClearable, isDisabled: isDisabled, isLoading: isLoading, isMulti: isMulti, isSearchable: isSearchable, menuPlacement: menuPlacement, menuPortalTarget: menuPortalTarget, menuShouldScrollIntoView: false, name: name, noOptionsMessage: handleNoOptionsMessage, onChange: onChange, onMenuOpen: calculateMenuPlacement, options: formattedOptions, placeholder: placeholder, ref: ref, styles: customStyles, value: selectedOptions }, props)) }));
};
const InputComponentRef = forwardRef(InputComponent);
const ReactSelectInputComponent = (_a, ref) => {
    var { ariaDescribedBy, ariaLabel, async, cacheOptions, clearable, components, creatable, defaultOptions, defaultValue, disabled, filterOption, icon, iconName, id, isAsync, isClearable, isCreatable, isDisabled, isLoading, isMulti, isOptionDisabled, isSearchable, label, labelPosition = 'top', loadOptions, menuPlacement, menuPortalTarget, menuPosition, noOptionsMessage, options, styles, variant = 'normal', width = '100%' } = _a, otherProps = __rest(_a, ["ariaDescribedBy", "ariaLabel", "async", "cacheOptions", "clearable", "components", "creatable", "defaultOptions", "defaultValue", "disabled", "filterOption", "icon", "iconName", "id", "isAsync", "isClearable", "isCreatable", "isDisabled", "isLoading", "isMulti", "isOptionDisabled", "isSearchable", "label", "labelPosition", "loadOptions", "menuPlacement", "menuPortalTarget", "menuPosition", "noOptionsMessage", "options", "styles", "variant", "width"]);
    return (_jsx(BaseInput, Object.assign({}, otherProps, { ariaDescribedBy: ariaDescribedBy, disabled: isDisabled || disabled, id: id, inputComponent: InputComponentRef, inputProps: Object.assign({ ariaDescribedBy, ariaLabel: ariaLabel || label, cacheOptions,
            components,
            defaultOptions,
            defaultValue,
            filterOption,
            icon,
            iconName,
            id, isAsync: isAsync || async, isClearable: isClearable || clearable, isCreatable: isCreatable || creatable, isDisabled: isDisabled || disabled, isLoading,
            isMulti,
            isOptionDisabled,
            isSearchable,
            loadOptions,
            menuPlacement,
            menuPortalTarget,
            menuPosition,
            noOptionsMessage,
            options,
            styles,
            variant }, otherProps), label: label, labelPosition: labelPosition, ref: ref, variant: variant, width: width })));
};
export const ReactSelectInput = forwardRef(ReactSelectInputComponent);
const NoOptionsMessageStyled = styled.span `
  color: ${({ theme }) => theme.colors.dove} !important;
`;
//# sourceMappingURL=index.js.map